// 치료 이력 타임라인 팝업 — 오가노이드 정보 > Patient Information > Treatment History 옆 버튼.
//   데이터는 정형화된 TreatmentHistory · TreatmentDrug (외부 전송과 같은 원본) 를 쓴다.
//   화면 그대로 PNG(3배율) · SVG 로 내려받는다.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { useTranslation } from 'react-i18next';
import { buildLayout, FONT } from './timelineLayout';
import TreatmentTimelineChart from './TreatmentTimelineChart';
import styles from './TreatmentTimelineModal.module.css';

let measureCtx = null;
function measure(text, size, weight = 400) {
    if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
    measureCtx.font = `${weight} ${size}px ${FONT}`;
    return measureCtx.measureText(String(text)).width;
}

function fileBase(key) {
    return String(key || 'organoid').replace('hashmark', '_').replace('#', '_') + '_treatment_timeline';
}

function saveBlob(blob, name) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function TreatmentTimelineModal({ show, onHide, organoidKey }) {
    const { t } = useTranslation();
    const svgRef = useRef(null);
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const [showRadiation, setShowRadiation] = useState(false);

    useEffect(() => {
        if (!show || !organoidKey) return;
        setData(null);
        setError(false);
        fetch('/api/getTreatmentTimeline', {
            method: 'post',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ key: organoidKey }),
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
            .then(setData)
            .catch(() => setError(true));
    }, [show, organoidKey]);

    const layout = useMemo(
        () => (data ? buildLayout(data, { showRadiation, measure, t }) : null),
        [data, showRadiation, t]
    );
    const hasProcs = !!data && (data.treatments || []).some((x) => x.type === 'radiation' || x.type === 'procedure');

    const svgString = () => {
        const node = svgRef.current;
        if (!node) return null;
        const clone = node.cloneNode(true);
        clone.setAttribute('width', String(layout.width));
        clone.setAttribute('height', String(layout.height));
        clone.removeAttribute('style');
        return '<?xml version="1.0" encoding="UTF-8"?>\n' + new XMLSerializer().serializeToString(clone);
    };

    const downloadSvg = () => {
        const s = svgString();
        if (s) saveBlob(new Blob([s], { type: 'image/svg+xml;charset=utf-8' }), fileBase(organoidKey) + '.svg');
    };

    const downloadPng = () => {
        const s = svgString();
        if (!s) return;
        const scale = 3;
        const img = new Image();
        const url = URL.createObjectURL(new Blob([s], { type: 'image/svg+xml;charset=utf-8' }));
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = layout.width * scale;
            canvas.height = layout.height * scale;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.scale(scale, scale);
            ctx.drawImage(img, 0, 0, layout.width, layout.height);
            URL.revokeObjectURL(url);
            canvas.toBlob((b) => b && saveBlob(b, fileBase(organoidKey) + '.png'), 'image/png');
        };
        img.onerror = () => URL.revokeObjectURL(url);
        img.src = url;
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered
            className={styles.ttModal} contentClassName={styles.ttContent} backdropClassName={styles.ttBackdrop}>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: '1.1rem' }}>{t('치료 이력 타임라인')}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ minHeight: 160 }}>
                {error && <div style={{ color: '#B42318' }}>{t('치료 이력을 불러오지 못했습니다')}</div>}
                {!error && !layout && <div style={{ color: '#888' }}>{t('불러오는 중')}…</div>}
                {layout && <TreatmentTimelineChart ref={svgRef} layout={layout} t={t} idPrefix="tt-modal" />}
            </Modal.Body>
            <Modal.Footer style={{ justifyContent: 'space-between' }}>
                <Form.Check
                    type="checkbox"
                    id="tt-show-radiation"
                    label={t('방사선·수술 표시')}
                    checked={showRadiation}
                    disabled={!hasProcs}
                    onChange={(e) => setShowRadiation(e.target.checked)}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button variant="outline-secondary" size="sm" disabled={!layout} onClick={downloadSvg}>SVG</Button>
                    <Button variant="primary" size="sm" disabled={!layout} onClick={downloadPng}>{t('이미지 다운로드')}</Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}

export default TreatmentTimelineModal;
